import React, { useEffect, useState } from "react";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import AdminNav from "../components/AdminNav";
import { adminService } from "../services/adminService";
import FormModal from "../components/FormModal";
import { toast } from "sonner";

const EMPTY_RULE = { label: "", metric: "temp", op: "above", value: 30, device: "All", channels: "push", order: 0, isPublished: true };

export default function AdminAlertsRulesPage() {
  const [rules, setRules] = useState([]);
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_RULE);
  const [editing, setEditing] = useState(null);

  async function load() {
    setError("");
    const res = await adminService.listAlertRulesAdmin();
    if (!res.ok) return setError(res.error || "Failed to load rules");
    setRules(res.data?.items || []);
  }

  useEffect(() => {
    load();
  }, []);

  async function saveNew() {
    const payload = {
      ...draft,
      value: Number(draft.value),
      channels: String(draft.channels || "").split(",").map((x) => x.trim()).filter(Boolean),
    };
    const res = await adminService.createAlertRule(payload);
    if (!res.ok) return toast.error(res.error || "Create failed");
    toast.success("Rule created");
    setAddOpen(false);
    setDraft(EMPTY_RULE);
    await load();
  }

  async function saveEdit() {
    if (!editing?._id) return;
    const payload = {
      ...editing,
      value: Number(editing.value),
      channels: Array.isArray(editing.channels)
        ? editing.channels
        : String(editing.channels || "").split(",").map((x) => x.trim()).filter(Boolean),
    };
    const res = await adminService.updateAlertRule(editing._id, payload);
    if (!res.ok) return toast.error(res.error || "Update failed");
    toast.success("Rule updated");
    setEditOpen(false);
    await load();
  }

  return (
    <PageShell>
      <PageHero eyebrow="Admin" title="Alert Rules" highlight="templates." description="Create and manage rule templates users can select from alerts page." />
      <PageSection variant="content">
        <div className="mx-auto max-w-7xl grid gap-6">
          <AdminNav />
          {error ? <Card><CardContent className="py-4 text-sm text-destructive">{error}</CardContent></Card> : null}
          <div><Button onClick={() => setAddOpen(true)}>Add Rule</Button></div>
          <div className="grid md:grid-cols-2 gap-6">
            {rules.map((r) => (
              <Card key={r._id} className="shadow-soft">
                <CardHeader><CardTitle>{r.label || `${r.metric} ${r.op} ${r.value}`}</CardTitle></CardHeader>
                <CardContent className="grid gap-2 text-sm">
                  <div className="text-muted-foreground">When <span className="text-foreground">{r.metric}</span> is <span className="text-foreground">{r.op}</span> <span className="text-foreground">{r.value}</span></div>
                  <div className="text-muted-foreground">{r.device || "All"} via {(r.channels || []).join(", ")}</div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setEditing({ ...r, channels: (r.channels || []).join(", ") }); setEditOpen(true); }}>Edit</Button>
                    <Button size="sm" variant="outline" onClick={async () => { const res = await adminService.deleteAlertRule(r._id); if (!res.ok) return toast.error(res.error || "Delete failed"); toast.success("Rule deleted"); await load(); }}>Delete</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </PageSection>

      <FormModal open={addOpen} title="Add Rule Template" onClose={() => setAddOpen(false)}>
        <div className="grid gap-3">
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Label" value={draft.label} onChange={(e) => setDraft((p) => ({ ...p, label: e.target.value }))} />
          <div className="grid grid-cols-3 gap-3">
            <select className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={draft.metric} onChange={(e) => setDraft((p) => ({ ...p, metric: e.target.value }))}>
              <option value="temp">temp</option>
              <option value="soil">soil</option>
              <option value="humidity">humidity</option>
            </select>
            <select className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={draft.op} onChange={(e) => setDraft((p) => ({ ...p, op: e.target.value }))}>
              <option value="above">above</option>
              <option value="below">below</option>
            </select>
            <input type="number" className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={draft.value} onChange={(e) => setDraft((p) => ({ ...p, value: e.target.value }))} />
          </div>
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Device (All or specific)" value={draft.device} onChange={(e) => setDraft((p) => ({ ...p, device: e.target.value }))} />
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Channels comma-separated (push,email,sms)" value={draft.channels} onChange={(e) => setDraft((p) => ({ ...p, channels: e.target.value }))} />
          <Button size="sm" onClick={saveNew}>Save Rule</Button>
        </div>
      </FormModal>

      <FormModal open={editOpen} title="Edit Rule Template" onClose={() => setEditOpen(false)}>
        <div className="grid gap-3">
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={editing?.label || ""} onChange={(e) => setEditing((p) => ({ ...(p || {}), label: e.target.value }))} />
          <div className="grid grid-cols-3 gap-3">
            <select className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={editing?.metric || "temp"} onChange={(e) => setEditing((p) => ({ ...(p || {}), metric: e.target.value }))}>
              <option value="temp">temp</option>
              <option value="soil">soil</option>
              <option value="humidity">humidity</option>
            </select>
            <select className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={editing?.op || "above"} onChange={(e) => setEditing((p) => ({ ...(p || {}), op: e.target.value }))}>
              <option value="above">above</option>
              <option value="below">below</option>
            </select>
            <input type="number" className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={editing?.value || 0} onChange={(e) => setEditing((p) => ({ ...(p || {}), value: e.target.value }))} />
          </div>
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={editing?.device || "All"} onChange={(e) => setEditing((p) => ({ ...(p || {}), device: e.target.value }))} />
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={editing?.channels || ""} onChange={(e) => setEditing((p) => ({ ...(p || {}), channels: e.target.value }))} />
          <Button size="sm" onClick={saveEdit}>Update Rule</Button>
        </div>
      </FormModal>
    </PageShell>
  );
}

